var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { Box, Grid, Button, Paper, Typography, Dialog, DialogContent, DialogActions, DialogTitle, Divider, Link, IconButton, } from '@mui/material';
import { Check, Clear } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
// Internal dependencies
import { appStyles } from '../App.styles';
import { UserContext, getAuthHeader } from '../auth';
import { axiosClient } from '../axios';
import { CLIENT_ENDPOINTS, ENDPOINTS, LOG_LEVELS, SERVICES, MULTILANGUAGE } from '../constants';
import { StripeLogoSG } from '../components';
import { subscriptionStyles } from './subscriptionStyles';
;
// 
export const Subscription = function (props) {
    var _a, _b;
    // Use translation
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    // Price tabs Monthly/Yearly
    const [priceTabs, setPriceTabs] = useState('Yearly');
    // Pricing plans
    const pricingPlans = [
        {
            name: 'Free',
            id: 'Free',
            monthlyPrice: 0,
            yearlyPrice: 0,
            monthlyLookupKey: 'monthlyFree1',
            yearlyLookupKey: 'yearlyFree1',
            subtitle: {
                monthly: 'No credit card required',
                yearly: 'No credit card required',
            },
            details: [
                `1${t('seat')}`,
                `2,000 ${t('words / Basic Models')}`,
                `1,000 ${t('words / Pro Models')}`,
                `5 ${t('SD images / month')}`,
            ],
        },
        {
            name: 'Starter',
            id: `${priceTabs} Starter`,
            monthlyPrice: 5,
            yearlyPrice: 48 / 12,
            monthlyLookupKey: 'monthlyStarter39',
            yearlyLookupKey: 'yearlyStarter370',
            subtitle: {
                monthly: 'Cancel anytime',
                yearly: 'Cancel anytime',
            },
            details: [
                `1${t('seat')}`,
                `25,000 ${t('words / Basic Models')}`,
                `10,000 ${t('words / Pro Models')}`,
                `10 ${t('SD/HD images / month')}`,
            ],
        },
        {
            name: 'Basic',
            id: `${priceTabs} Basic`,
            monthlyPrice: 10,
            yearlyPrice: 96 / 12,
            monthlyLookupKey: 'monthlyBasic78',
            yearlyLookupKey: 'yearlyBasic745',
            subtitle: {
                monthly: 'Cancel anytime',
                yearly: 'Cancel anytime',
            },
            details: [
                `1${t('seat')}`,
                `60,000 ${t('words / Basic Models')}`,
                `30,000 ${t('words / Pro Models')}`,
                `20 ${t('SD/HD images / month')}`,
            ],
        },
        {
            name: 'Basic Plus',
            id: `${priceTabs} Basic Plus`,
            monthlyPrice: 15,
            yearlyPrice: 144 / 12,
            monthlyLookupKey: 'monthlyBasicPlus116',
            yearlyLookupKey: 'yearlyBasicPlus1118',
            subtitle: {
                monthly: 'Cancel anytime',
                yearly: 'Cancel anytime',
            },
            details: [
                `1${t('seat')}`,
                `100,000 ${t('words / Basic Models')}`,
                `50,000 ${t('words / Pro Models')}`,
                `30 ${t('SD/HD images / month')}`,
            ],
        },
        {
            name: 'Individual',
            id: `${priceTabs} Individual`,
            monthlyPrice: 19,
            yearlyPrice: 180 / 12,
            monthlyLookupKey: 'monthlyIndividual148',
            yearlyLookupKey: 'yearlyIndividual1399',
            subtitle: {
                monthly: 'Cancel anytime',
                yearly: 'Cancel anytime',
            },
            details: [
                `1${t('seat')}`,
                _jsx(Trans, { i18nKey: 'Unlimited words', default: 'Unlimited words', components: { strong: _jsx("strong", {}) } }),
                t('Prompt Library & Brand Voice'),
                `50 ${t('SD/HD images / month')}`,
            ],
        },
        {
            name: 'Teams',
            id: `${priceTabs} Teams`,
            monthlyPrice: 39,
            yearlyPrice: 384 / 12,
            monthlyLookupKey: 'monthlyCreator299',
            yearlyLookupKey: 'yearlyCreator2990',
            subtitle: {
                monthly: 'Cancel anytime',
                yearly: 'Cancel anytime',
            },
            details: [
                `4${t('seat')}`,
                _jsx(Trans, { i18nKey: 'Unlimited words', default: 'Unlimited words', components: { strong: _jsx("strong", {}) } }),
                t('Prompt Library & Brand Voice'),
                `100 ${t('SD/HD images / month')}`,
            ],
        },
        // Temporary hide
        /*
        {
          name: 'Enterprise',
          id: `${priceTabs} Enterprise`,
          monthlyPrice: -1,
          yearlyPrice: -1,
          monthlyLookupKey: 'monthlyEnterprise2000',
          yearlyLookupKey: 'yearlyEnterprise20000',
          subtitle: {
            monthly: 'For organizations to deploy AI at scale',
            yearly: 'For organizations to deploy AI at scale',
          },
          details: [
            t('Multiple seats'),
            t('Unlimited usage'),
            t('Custom templates'),
            t('Dedicated account manager'),
          ],
        },
        */
    ];
    // Get user credential data
    const user = useContext(UserContext);
    // Subscription plan
    const [subscriptionPlan, setSubscriptionPlan] = useState(pricingPlans[0].id);
    // Change Plan Dialog open status
    const [dialogOpen, setDialogOpen] = useState(false);
    // Handle Change Plan dialog close
    const handleDialogClose = () => {
        setDialogOpen(false);
    };
    // Pricing plan
    const [pricingPlan, setPricingPlan] = useState(pricingPlans[0]);
    // New plan period start epoch time
    const [newPlanPeriodStart, setNewPlanPeriodStart] = useState(0);
    // Handle change plan click
    const handleChangePlanClick = (pricingPlan) => __awaiter(this, void 0, void 0, function* () {
        var _c, _d, _e, _f, _g, _h, _j;
        if (!user) {
            navigate(CLIENT_ENDPOINTS.SIGN_IN, { state: { pathname: location.pathname, search: location.search } });
            return;
        }
        try {
            // Update pricing plan
            setPricingPlan(pricingPlan);
            // Update Change Plan Button loading status
            setChangePlanLoading(true);
            // Get autheHeader
            const authHeader = yield getAuthHeader(user);
            // Get header
            const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Get lookup key
            const lookupKey = priceTabs === 'Monthly' ? pricingPlan.monthlyLookupKey : pricingPlan.yearlyLookupKey;
            // Params
            const params = {
                lookupKey,
                successURL: `${process.env.CLIENT_URL}${CLIENT_ENDPOINTS.SETTINGS}`,
                cancelURL: `${process.env.CLIENT_URL}${CLIENT_ENDPOINTS.SETTINGS}`,
                locale: i18n.language,
                promoteKitReferral: window === null || window === void 0 ? void 0 : window.promotekit_referral,
            };
            // Get response
            const res = yield axiosClient.post(`${process.env.BILLING_SERVER_URL}${ENDPOINTS.BILLING}${ENDPOINTS.CREATE_CHECKOUT_SESSION}`, params, { headers });
            // Update Change Plan Button loading status
            setChangePlanLoading(false);
            const { sessionURL } = (_c = res === null || res === void 0 ? void 0 : res.data) === null || _c === void 0 ? void 0 : _c.data;
            // Check if session URL exist
            if (!sessionURL) {
                const { type } = (_d = res === null || res === void 0 ? void 0 : res.data) === null || _d === void 0 ? void 0 : _d.data;
                if (type === 'upgrade') {
                    const { currentPlanProration, targetPlanProration, total, appliedCreditBalance, amountDueToday, prorationDate, } = (_e = res === null || res === void 0 ? void 0 : res.data) === null || _e === void 0 ? void 0 : _e.data;
                    setProration({
                        currentPlanProration,
                        targetPlanProration,
                        total,
                        appliedCreditBalance,
                        amountDueToday,
                        prorationDate,
                    });
                    setUpgrade(true);
                }
                else {
                    // If downgrade
                    // Extract & update new plan period start
                    const { newPlanPeriodStart, } = (_f = res === null || res === void 0 ? void 0 : res.data) === null || _f === void 0 ? void 0 : _f.data;
                    setNewPlanPeriodStart(newPlanPeriodStart);
                    // Update upgrade
                    setUpgrade(false);
                }
                ;
                // Show confirm dialog
                setDialogOpen(true);
                return;
            }
            ;
            // If yes, redirect to new checkout session URL
            window.location.href = sessionURL;
        }
        catch (err) {
            // Update Change Plan Button loading status
            setChangePlanLoading(false);
            // Save log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Subscription, handleChangePlanClick, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.BILLING,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_g = err === null || err === void 0 ? void 0 : err.response) === null || _g === void 0 ? void 0 : _g.status) {
                // If yes, notify user with the server error message
                toast.error((_j = (_h = err === null || err === void 0 ? void 0 : err.response) === null || _h === void 0 ? void 0 : _h.data) === null || _j === void 0 ? void 0 : _j.message);
                return;
            }
            ;
            // Notify user
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    // Upgrade/downgrade state
    const [upgrade, setUpgrade] = useState(false);
    // Proration information
    const [proration, setProration] = useState({});
    // Change plan
    const changePlan = () => __awaiter(this, void 0, void 0, function* () {
        var _k, _l, _m;
        try {
            // Update Change Plan Button loading status
            setChangePlanLoading(true);
            // Get autheHeader
            const authHeader = yield getAuthHeader(user);
            // Get headers
            const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Get lookup key
            const lookupKey = priceTabs === 'Monthly' ? pricingPlan.monthlyLookupKey : pricingPlan.yearlyLookupKey;
            // Params
            const params = {
                lookupKey,
                prorationDate: proration === null || proration === void 0 ? void 0 : proration.prorationDate,
            };
            // Get response
            const res = yield axiosClient.post(`${process.env.BILLING_SERVER_URL}${ENDPOINTS.BILLING}${ENDPOINTS.UPDATE_PLAN}`, params, { headers });
            // Update Change Plan Button loading status
            setChangePlanLoading(false);
            // If no, reload the page to update new data
            window.location.href = window.location.href;
        }
        catch (err) {
            // Update Change Plan Button loading status
            setChangePlanLoading(false);
            // Save log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Subscription, changePlan, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.BILLING,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_k = err === null || err === void 0 ? void 0 : err.response) === null || _k === void 0 ? void 0 : _k.status) {
                // If yes, notify user with the server error message
                toast.error((_m = (_l = err === null || err === void 0 ? void 0 : err.response) === null || _l === void 0 ? void 0 : _l.data) === null || _m === void 0 ? void 0 : _m.message);
                return;
            }
            ;
            // Notify user
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    // Load usage
    useEffect(() => {
        const getUsage = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                if (!user) {
                    return;
                }
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get current month, year
                const now = new Date();
                const month = now.getMonth() + 1;
                const year = now.getFullYear();
                // Get usage
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.USAGE}${ENDPOINTS.GET_USAGE}?month=${month}&year=${year}`, { headers });
                const { subscriptionPlan } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                setSubscriptionPlan(subscriptionPlan);
            }
            catch (err) {
                // Save log
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Subscription, getUsage, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.BILLING,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        getUsage();
    }, [user]);
    const [pricesInUSD, setPricesInUSD] = useState([]);
    useEffect(() => {
        // Get Pricing from pricing plan
        const getPricesInUSD = () => {
            try {
                const pricesInUSD = pricingPlans.map((pricingPlan) => {
                    const rawMonthlyPrice = pricingPlan === null || pricingPlan === void 0 ? void 0 : pricingPlan.monthlyPrice;
                    let monthlyPrice = '';
                    if (rawMonthlyPrice < 0) {
                        monthlyPrice = t('Custom Price');
                    }
                    else if (rawMonthlyPrice === 0) {
                        monthlyPrice = t('Free');
                    }
                    else {
                        monthlyPrice = `$${rawMonthlyPrice}/${t('mo')}`;
                    }
                    ;
                    const rawYearlyPrice = pricingPlan === null || pricingPlan === void 0 ? void 0 : pricingPlan.yearlyPrice;
                    let yearlyPrice = '';
                    if (rawYearlyPrice < 0) {
                        yearlyPrice = t('Custom Price');
                    }
                    else if (rawYearlyPrice === 0) {
                        yearlyPrice = t('Free');
                    }
                    else {
                        yearlyPrice = `$${rawYearlyPrice}/${t('mo')}`;
                    }
                    ;
                    return ({ monthlyPrice, yearlyPrice });
                });
                setPricesInUSD([...pricesInUSD]);
            }
            catch (err) {
                // Save log
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Subscription, getPricesInUSD, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.BILLING,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        };
        getPricesInUSD();
    }, [i18n.language]);
    // Get Pricing from pricing plan
    const getPricing = (idx) => {
        var _a, _b;
        // If price tab is monthly
        if (priceTabs === 'Monthly') {
            return ((_a = pricesInUSD[idx]) === null || _a === void 0 ? void 0 : _a.monthlyPrice) || '';
        }
        else {
            return ((_b = pricesInUSD[idx]) === null || _b === void 0 ? void 0 : _b.yearlyPrice) || '';
        }
    };
    /**
     * Get Original monthly pricing
     * @param idx
     */
    const getOriginalMonthlyPricing = (idx) => {
        const discountMonthlyPrice = pricingPlans[idx].monthlyPrice;
        const originalMonthlyPrice = Math.round(discountMonthlyPrice / 80 * 100);
        return `$${originalMonthlyPrice}`;
    };
    // Loading status of Change Plan button
    const [changePlanLoading, setChangePlanLoading] = useState(false);
    // Locale date format
    let dateFormat = 'DD MMM YYYY';
    switch (i18n.language) {
        case MULTILANGUAGE.en.value:
            dateFormat = 'DD MMM YYYY';
            break;
        case MULTILANGUAGE.ja.value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.ko.value:
            dateFormat = 'YYYY년 M월 D일';
            break;
        case MULTILANGUAGE['zh-CN'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE['zh-HK'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.id.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.th.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.vi.value:
            dateFormat = 'DD MMMM YYYY';
            break;
    }
    ;
    return (_jsxs(Box
    // Subscription container
    , Object.assign({ 
        // Subscription container
        sx: {
            width: '100%',
            height: '100%',
            padding: '2px',
        } }, { children: [_jsxs(Grid
            // Price tabs flex container
            , Object.assign({ 
                // Price tabs flex container
                container: true }, { children: [_jsx(Grid
                    // Monthly flex item
                    , Object.assign({ 
                        // Monthly flex item
                        item: true }, { children: _jsx(Button, Object.assign({ variant: "outlined", sx: Object.assign(Object.assign({}, subscriptionStyles.pricingTab), { backgroundColor: priceTabs === 'Monthly' ? '#CEE9DD' : 'transparent', '&:hover': {
                                    backgroundColor: priceTabs === 'Monthly' ? '#CEE9DD' : 'transparent',
                                } }), onClick: () => {
                                setPriceTabs('Monthly');
                            }, startIcon: _jsx(Check, { sx: {
                                    display: priceTabs === 'Monthly' ? 'inline-block' : 'none',
                                    color: '#191C1B',
                                } }) }, { children: t('Monthly') })) })), _jsx(Grid
                    // Yearly flex item
                    , Object.assign({ 
                        // Yearly flex item
                        item: true, sx: {
                            padding: '0 0 1rem',
                        } }, { children: _jsx(Button, Object.assign({ variant: "outlined", sx: Object.assign(Object.assign({}, subscriptionStyles.pricingTab), { borderRadius: '0 16px 16px 0', backgroundColor: priceTabs === 'Yearly' ? '#CEE9DD' : 'transparent', '&:hover': {
                                    backgroundColor: priceTabs === 'Yearly' ? '#CEE9DD' : 'transparent',
                                } }), onClick: () => { setPriceTabs('Yearly'); }, startIcon: _jsx(Check, { sx: { display: priceTabs === 'Yearly' ? 'inline-block' : 'none' } }) }, { children: t('Yearly') })) }))] })), _jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { textAlign: 'left', padding: '0 0 1rem' }) }, { children: [t('All plans include unlimited access to Incognito mode.') + ' ', _jsx(Trans, { i18nKey: "<strong><em>Pro Models</em></strong> include GPT 4 Turbo, GPT 4o, Claude Sonnet 3.5 & AI Search.", defaults: "<strong><em>Pro Models</em></strong> include GPT 4 Turbo, GPT 4o, Claude Sonnet 3.5 & AI Search.", components: { strong: _jsx("strong", {}), em: _jsx("em", {}) } })] })), _jsx(Box
            // Pricing plans container
            , Object.assign({ 
                // Pricing plans container
                sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 0 1.5rem',
                } }, { children: _jsx(Grid
                // Pricing plans flex container
                , Object.assign({ 
                    // Pricing plans flex container
                    container: true, spacing: 3, sx: {
                        maxWidth: '1296px',
                        width: '100%',
                        justifyContent: 'center',
                    } }, { children: pricingPlans.map((pricingPlan, idx) => (_jsx(Grid
                    // Pricing plan flex item
                    , Object.assign({ 
                        // Pricing plan flex item
                        item: true, xs: 12, sm: 6, md: (subscriptionPlan !== pricingPlans[0].id) ? 4 : 4, sx: {
                            alignItems: 'stretch',
                            display: (subscriptionPlan !== pricingPlans[0].id && pricingPlan.id === pricingPlans[0].id) ? 'none' : 'flex',
                        } }, { children: _jsxs(Paper, Object.assign({ sx: {
                                padding: '1.5rem 1.5rem 1rem',
                                borderRadius: '28px',
                                '&:hover': {
                                    border: '1px solid #006B54',
                                },
                                backgroundColor: '#F8FAF7',
                                width: '100%',
                                boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
                            } }, { children: [_jsx(Typography
                                // Pricing plan title
                                , Object.assign({ 
                                    // Pricing plan title
                                    sx: Object.assign(Object.assign({}, appStyles.text24), { textAlign: 'center', width: '100%', padding: '0 0 1rem', fontWeight: '500', color: '#191C1B' }) }, { children: t(pricingPlan.name) })), _jsxs(Grid, Object.assign({ container: true, columnSpacing: 1, sx: {
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        margin: '0 0 1rem -0.5rem',
                                    } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'inline-flex' } }, { children: _jsx(Typography
                                            // Price
                                            , Object.assign({ 
                                                // Price
                                                sx: Object.assign(Object.assign({}, appStyles.text24), { fontWeight: '700', textAlign: 'center', color: '#191C1B' }) }, { children: getPricing(idx) })) })), _jsx(Grid, Object.assign({ item: true, sx: { display: pricingPlan.id === pricingPlans[0].id ? 'none' : 'inline-flex' } }, { children: _jsx(Typography
                                            // Price
                                            , Object.assign({ 
                                                // Price
                                                sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'center', color: 'greenDarker.main' }) }, { children: _jsx("s", { children: getOriginalMonthlyPricing(idx) }) })) }))] })), _jsxs(Box, Object.assign({ sx: {
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    } }, { children: [_jsx(Button, Object.assign({ variant: "contained", sx: Object.assign(Object.assign({}, subscriptionStyles.pricingButton), { display: subscriptionPlan === pricingPlan.id ? 'flex' : 'none', backgroundColor: '#CEE9DD', color: '#082018', cursor: 'auto', '&:hover': {
                                                    backgroundColor: '#CEE9DD',
                                                    boxShadow: 'none',
                                                } }) }, { children: t('Current Plan') })), _jsx(Button, Object.assign({ variant: "contained", sx: Object.assign(Object.assign({}, subscriptionStyles.pricingButton), { display: ((pricingPlan.monthlyPrice >= 0) && (subscriptionPlan !== pricingPlan.id)) ? 'flex' : 'none' }), onClick: () => handleChangePlanClick(pricingPlan) }, { children: t('Update') })), _jsx(Button, Object.assign({ variant: "contained", sx: Object.assign(Object.assign({}, subscriptionStyles.pricingButton), { display: (pricingPlan.monthlyPrice < 0) && (subscriptionPlan !== pricingPlan.id) ? 'flex' : 'none' }), onClick: () => __awaiter(this, void 0, void 0, function* () {
                                                try {
                                                    window.location.href = 'mailto:hey@studioglobal.ai';
                                                }
                                                catch (err) {
                                                    // Save log
                                                    axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                                                        level: LOG_LEVELS.ERROR,
                                                        logMessage: `Subscription, contactUs, ${err === null || err === void 0 ? void 0 : err.message}`,
                                                        metadata: {
                                                            env: process.env.ENV,
                                                            service: SERVICES.BILLING,
                                                            userId: user === null || user === void 0 ? void 0 : user.uid,
                                                            email: user === null || user === void 0 ? void 0 : user.email,
                                                            userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                                                        },
                                                    });
                                                    // Notify user
                                                    toast.error(t('Something went wrong. We are figuring it out.'));
                                                }
                                                ;
                                            }) }, { children: t('Contact Us') }))] })), _jsx(Typography
                                // Subtitle
                                , Object.assign({ 
                                    // Subtitle
                                    sx: Object.assign(Object.assign({}, appStyles.text14), { color: '#3F4945', padding: '0 0 1.5rem', textAlign: 'center' }) }, { children: t(priceTabs === 'Monthly' ? pricingPlan.subtitle.monthly : pricingPlan.subtitle.yearly) })), pricingPlan.details.map((detail, idx) => (
                                // Pricing details
                                _jsxs(Grid, Object.assign({ container: true, spacing: 2, sx: { alignItems: 'center', flexWrap: 'wrap' } }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Check, { sx: { color: '#191C1B' } }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { width: '100%', textAlign: 'left', padding: '1rem 0', overflowWrap: 'break-word', color: '#191C1B', whiteSpace: 'pre-wrap' }) }, { children: detail })) }))] }), `${detail}-${idx}`)))] })) }), pricingPlan.id))) })) })), _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { padding: '1rem 0 0', color: '#3F4945', textAlign: 'left', width: '100%', display: 'none' }) }, { children: t(`*By opting for the Individual Plan, you'll gain access to unlimited messaging on GPT-4 and GPT-3.5. For GPT-4, it maintains consistent performance for up to 100,000 words monthly. Beyond this number of messages, usage might impact performance, including speed.`) })), _jsxs(Dialog
            //Update subscription plan dialog
            , Object.assign({ 
                //Update subscription plan dialog
                sx: {
                    '& .MuiDialog-paper': {
                        backgroundColor: '#ffffff',
                        border: 'none',
                        boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
                        borderRadius: '1rem',
                        margin: '0.5rem',
                        width: {
                            xs: '100%',
                            md: '70%',
                        },
                        maxWidth: '720px',
                    },
                }, open: dialogOpen, onClose: handleDialogClose }, { children: [_jsx(DialogTitle, Object.assign({ sx: {
                            padding: '1.5rem',
                        } }, { children: _jsxs(Grid
                        // Dialog title flex container
                        , Object.assign({ 
                            // Dialog title flex container
                            container: true }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true, sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                    } }, { children: _jsx(Typography
                                    // Title
                                    , Object.assign({ 
                                        // Title
                                        sx: Object.assign(Object.assign({}, appStyles.text24), { color: '#191C1B' }) }, { children: t('Change subscription for Studio Global') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(IconButton, Object.assign({ onClick: handleDialogClose }, { children: _jsx(Clear, { sx: { color: '#1C1B1F' } }) })) }))] })) })), _jsxs(DialogContent
                    // Dialog content 
                    , Object.assign({ 
                        // Dialog content 
                        sx: {
                            padding: '0 1.5rem',
                            overflow: 'none',
                            boxSizing: 'border-box',
                        } }, { children: [_jsx(Box
                            // Downgrade Confirm UI
                            , Object.assign({ 
                                // Downgrade Confirm UI
                                sx: {
                                    display: upgrade ? 'none' : 'block',
                                    padding: '0 0 1.5rem',
                                } }, { children: _jsx(Box
                                // Confirm message
                                , Object.assign({ 
                                    // Confirm message
                                    sx: { display: 'block' } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { display: 'inline' }) }, { children: _jsx(Trans, { i18nKey: 'Your new plan will start at <strong><em>{{date}}</em></strong>, and the current plan’s usage limit will remain until the new plan becomes effective.', default: 'Your new plan will start at <strong><em>{{date}}</em></strong>, and the current plan’s usage limit will remain until the new plan becomes effective.', components: { strong: _jsx("strong", {}), em: _jsx("em", {}) }, values: {
                                                date: moment.unix(newPlanPeriodStart).local().locale(i18n.language).format(dateFormat),
                                            } }) })) })) })), _jsxs(Box
                            // Upgrade confirm block
                            , Object.assign({ 
                                // Upgrade confirm block
                                sx: {
                                    display: upgrade ? 'block' : 'none',
                                } }, { children: [_jsx(Box
                                    // Confirm message
                                    , Object.assign({ 
                                        // Confirm message
                                        sx: {
                                            display: 'inline-block',
                                            padding: '0 0 1.5rem',
                                        } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { display: 'inline' }) }, { children: _jsx(Trans, { i18nKey: 'Your new plan will start <strong><em>immediately</em></strong>, and your card will be charged a pro-rated amount for the current billing period.', default: 'Your new plan will start <strong><em>immediately</em></strong>, and your card will be charged a pro-rated amount for the current billing period.', components: { strong: _jsx("strong", {}), em: _jsx("em", {}) } }) })) })), _jsxs(Grid
                                    // Prorated credit flex container
                                    , Object.assign({ 
                                        // Prorated credit flex container
                                        container: true, columnSpacing: 3, sx: {
                                            padding: '0 0 1rem',
                                        } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', fontStyle: 'italic' }) }, { children: t('Pro-rated credit from current plan:') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', color: '#3F4945' }) }, { children: ["-HK$", (Math.abs((_a = proration === null || proration === void 0 ? void 0 : proration.currentPlanProration) === null || _a === void 0 ? void 0 : _a.amount) / 100).toFixed(2)] })) }))] })), _jsxs(Grid
                                    // Prorated charge flex container
                                    , Object.assign({ 
                                        // Prorated charge flex container
                                        container: true, columnSpacing: 3, sx: { padding: '0 0 1rem' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', fontStyle: 'italic' }) }, { children: t('Pro-rated charge for new plan:') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', color: '#3F4945' }) }, { children: ["\u00A0\u00A0HK$", (((_b = proration === null || proration === void 0 ? void 0 : proration.targetPlanProration) === null || _b === void 0 ? void 0 : _b.amount) / 100).toFixed(2)] })) }))] })), _jsxs(Grid, Object.assign({ container: true, columnSpacing: 3, sx: { padding: '0 0 1rem' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', fontStyle: 'italic' }) }, { children: t('Applied account balance of $0') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', color: '#3F4945' }) }, { children: ["-HK$", (Math.abs(proration === null || proration === void 0 ? void 0 : proration.appliedCreditBalance) / 100).toFixed(2)] })) }))] })), _jsxs(Grid
                                    // Amount due today flex container
                                    , Object.assign({ 
                                        // Amount due today flex container
                                        container: true, columnSpacing: 3, sx: { padding: '0 0 1.5rem' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', fontWeight: '700', fontStyle: 'italic' }) }, { children: t('Amount due today:') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text16), { textAlign: 'left', fontWeight: '700', color: '#3F4945' }) }, { children: ["\u00A0\u00A0HK$", ((proration === null || proration === void 0 ? void 0 : proration.amountDueToday) / 100).toFixed(2)] })) }))] }))] })), _jsx(Divider
                            // Horizontal bar
                            , { 
                                // Horizontal bar
                                sx: {
                                    width: '100%',
                                    borderTop: '0',
                                    borderBottom: '1px solid #BFC9C3',
                                    boxSizing: 'border-box',
                                    margin: '0 0 1.5rem',
                                } }), _jsx(Typography
                            // Confirm texti
                            , Object.assign({ 
                                // Confirm texti
                                sx: Object.assign(Object.assign({}, appStyles.text12), { color: '#3F4945' }) }, { children: _jsx(Trans, { i18nKey: `By confirming your new plan, you agree to Studio Global's <Link href='#' color='inherit'>Terms of Service</Link> and <Link href='#' color='inherit'>Privacy Policy.</Link>`, default: `By confirming your new plan, you agree to Studio Global's <Link href='#' color='inherit'>Terms of Service</Link> and <Link href='#' color='inherit'>Privacy Policy.</Link>`, components: { Link: _jsx(Link, {}) } }) }))] })), _jsxs(DialogActions, Object.assign({ sx: { display: 'block', padding: '0 1.5rem 1.5rem' } }, { children: [_jsx(Box, Object.assign({ sx: { display: 'flex', justifyContent: 'flex-end', padding: '1rem 0 0' } }, { children: _jsx(LoadingButton
                                // Confirm loading buttoni
                                , Object.assign({ 
                                    // Confirm loading buttoni
                                    variant: "contained", loading: changePlanLoading, sx: {
                                        borderRadius: '100px',
                                        textTransform: 'none',
                                        boxShadow: 'none',
                                        padding: '0.625rem 1.5rem',
                                    }, onClick: changePlan }, { children: t('Confirm') })) })), _jsxs(Box
                            // Secure by text
                            , Object.assign({ 
                                // Secure by text
                                sx: { display: 'flex', justifyContent: 'flex-end', padding: '1rem 0 0' } }, { children: [_jsxs(Typography, Object.assign({ sx: {
                                            fontSize: '11px',
                                            fontWeight: '500',
                                            fontFamily: 'DM Sans',
                                        } }, { children: [t('Secured by'), "\u00A0"] })), _jsx(StripeLogoSG, { sx: { width: '27px', height: '16.5px' } })] }))] }))] }))] })));
};
